/**
 * Sheet „Mieter anlegen" (`tenant-sheet`, Spec 03, „Sheet Mieter anlegen").
 *
 * Öffnet aus dem Vertragssheet heraus (`contract-create-tenant-new`,
 * `features/contracts`) — dieses Feature kennt `features/contracts` nicht
 * (`apps/web/CLAUDE.md`, „features kennen einander nicht"); wer beide Sheets
 * gleichzeitig offen hält, komponiert in der Route
 * (`routes/ObjectDetail.tsx`), genau wie `MoveSheet` und `UnitSheet` es schon
 * mit `features/objects`/`features/properties` tun.
 *
 * Drei Felder, **kein** „Anlegen und noch eins" (Spec 03, „Sheet Vertrag
 * anlegen": bewusste Abweichung vom 02a-Muster, gilt auch hier). Über dem
 * Namensfeld steht der Satz zu Entscheidung 6 — der einzige Ort, an dem die
 * Regel „ein Mieter ist eine Mietpartei" dem Nutzer begegnet.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import type { components } from '@/lib/api-types'
import { useCreateTenant } from '@/features/tenants/api'
import type { TenantCreate } from '@/features/tenants/api'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { LegalFormField } from '@/features/tenants/LegalFormField'
import { SalutationField } from '@/features/tenants/SalutationField'

/** Was der Aufrufer nach dem Anlegen braucht, um den neuen Mieter in seiner
 * eigenen Auswahl vorzubelegen — dieselbe Form wie ein Treffer aus
 * `GET /tenants?q=`. */
export interface CreatedTenant {
  readonly id: string
  readonly label: string
  readonly salutation: components['schemas']['LabeledValue'] | null
}

interface TenantCreateSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onCreated: (tenant: CreatedTenant) => void
}

const DEFAULT_LEGAL_FORM = 'person'

const EMPTY_FORM = {
  displayName: '',
  salutation: '',
  legalForm: DEFAULT_LEGAL_FORM,
}

type FormValues = typeof EMPTY_FORM

function toBody(form: FormValues): TenantCreate {
  return {
    display_name: form.displayName,
    legal_form: form.legalForm as TenantCreate['legal_form'],
    ...(form.salutation === '' ? {} : { salutation: form.salutation as NonNullable<TenantCreate['salutation']> }),
  }
}

export function TenantCreateSheet({ open, onOpenChange, onCreated }: TenantCreateSheetProps) {
  const createMutation = useCreateTenant()
  const displayNameRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState<FormValues>(EMPTY_FORM)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt) — dieselbe Technik
  // wie in `BuildingSheet`/`UnitSheet`.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(EMPTY_FORM)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateDisplayName(value: string) {
    setForm((current) => ({ ...current, displayName: value }))
  }

  function updateSalutation(value: string) {
    setForm((current) => ({ ...current, salutation: value }))
  }

  /** „Firma" schaltet die Anrede ab" (AK-202) — Formularverhalten, keine
   * zweite Wahrheit: Die Regel steht im Server (422) und im
   * Check-Constraint, hier erspart sie nur den Fehlversuch. */
  function updateLegalForm(value: string) {
    setForm((current) => ({
      ...current,
      legalForm: value,
      salutation: value === 'company' ? '' : current.salutation,
    }))
  }

  async function submit() {
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({ body: toBody(form) })
      onOpenChange(false)
      onCreated({ id: created.id, label: created.label, salutation: created.salutation })
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  const isCompany = form.legalForm === 'company'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          displayNameRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form
          data-testid="tenant-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Mieter anlegen</SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {/* Entscheidung 6, wörtlich — der einzige Ort, an dem sie dem
             * Nutzer begegnet (Spec 03, Nachtrag „Bleibt das Vertragsanlegen
             * bei vier Feldern?"). */}
            <p className="text-label text-muted">
              Eine Mietpartei, ein Mieter. Mieten Eheleute gemeinsam, ist das ein Eintrag mit der Anrede „Eheleute".
            </p>

            <FormField
              ref={displayNameRef}
              label="Name"
              testId="tenant-create-display-name"
              apiField="display_name"
              errorPrefix="tenant-create"
              required
              value={form.displayName}
              onChange={updateDisplayName}
              error={fieldError}
            />

            <SalutationField
              testId="tenant-create-salutation"
              errorPrefix="tenant-create"
              value={form.salutation}
              onChange={updateSalutation}
              error={fieldError}
              disabled={isCompany}
            />

            <LegalFormField
              testId="tenant-create-legal-form"
              errorPrefix="tenant-create"
              value={form.legalForm}
              onChange={updateLegalForm}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="tenant-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="tenant-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="tenant-create-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Mieter anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
